import { json } from "@remix-run/node";

import {
  createSeoMetaData,
  getArtworkFromData,
  getRootLoaderDataFromMatches,
} from "~/utils/data";
import { vttToTranscript } from "~/utils/transcript";
import { createPlaylistFromSession } from "~/utils/media";
import {
  bookmarkSession,
  getSessionByPosition,
  markSessionComplete,
  unbookmarkSession,
  unmarkSessionComplete,
  updateSessionProgress,
} from "~/models/session.server";
import { getClassItems } from "~/models/class.server";
import {
  createNote,
  deleteNote,
  getNotes,
  saveReflectionResponse,
  updateNote,
} from "~/models/notebook.server";
import { Action } from "~/constants";
import { formatDuration } from "~/utils/helpers";
import { translate } from "~/components/i18n/helpers";
import { logger } from "~/utils/logging";

import type {
  MetaArgs,
  LoaderFunctionArgs,
  ActionFunctionArgs,
} from "@remix-run/node";
import type {
  Session,
  Class,
  Scalars,
  Note,
  ClassroomImage,
} from "~/graphql/types";
import type { MediaChapter } from "~/components/media/types";
import type { VideoPlaylistEntry } from "~/components/video/Video";
import type { TranscriptData } from "~/utils/transcript";

export interface SessionRouteLoaderData {
  classItems: Partial<Class>;
  notes: Note[] | null;
  session: Session;
  transcript: TranscriptData | null;
  chapters?: MediaChapter[];
  playlist: VideoPlaylistEntry[];
  startTime: number;
}

export const handle = {
  includeGlobalUI: false,
};

export async function loader({ request, params, context }: LoaderFunctionArgs) {
  const position = Number(params.session);

  const session = await getSessionByPosition(
    context.graphql,
    params.class!,
    position,
  );

  if (!session) {
    throw new Response("Not Found", {
      status: 404,
    });
  }

  const { searchParams } = new URL(request.url);

  const [classItems, transcript, notes] = await Promise.all([
    getClassItems(context.graphql, session.class?.slug!).catch(() => ({})),
    fetch(session.captionsUrl!)
      .then((r) => r.text())
      .then(vttToTranscript)
      .catch(() => null),
    getNotes(context.graphql, params.class!, session.id!).catch(() => null),
  ]);

  const chapters = session.slides?.map((s) => ({ timestamp: s.timestamp }));

  const playlist = createPlaylistFromSession(session);

  // Compute the start time, using search params if available.if (searchParams.has("t")) {
  const getStartTime = () => {
    // Use the `?t=` param if available
    if (searchParams.has("t")) {
      return Number(searchParams.get("t"));
    }

    // Start at the begining if the session is complete
    if (session.user?.isComplete) {
      return 0;
    }

    return session.user?.currentTimestamp ?? 0;
  };

  return json<SessionRouteLoaderData>({
    classItems,
    notes,
    session,
    transcript,
    chapters,
    playlist,
    startTime: getStartTime(),
  });
}

export async function action({ request, context }: ActionFunctionArgs) {
  const formData = await request.formData();
  const intent = formData.get("intent");

  switch (intent) {
    case Action.BookmarkSession:
      return bookmarkSession(
        context.graphql,
        formData.get("sessionId") as Scalars["ID"]["input"],
      );
    case Action.UnbookmarkSession:
      return unbookmarkSession(
        context.graphql,
        formData.get("sessionId") as Scalars["ID"]["input"],
      );
    case Action.UpdateSessionProgress:
      return updateSessionProgress(
        context.graphql,
        formData.get("sessionId") as Scalars["ID"]["input"],
        Number(formData.get("timestamp")),
      ).catch((error) => {
        // TODO: Expand this pattern of catching action
        // errors in order to prevent failed actions from
        // breaking the app.

        // TODO: Monitor this error in NR. It's likely an
        // issue with Zed stability.
        logger.error("updateSessionProgress failed", error);

        return null;
      });
    case Action.MarkSessionComplete:
      return markSessionComplete(
        context.graphql,
        formData.get("sessionId") as Scalars["ID"]["input"],
      );
    case Action.UnmarkSessionComplete:
      return unmarkSessionComplete(
        context.graphql,
        formData.get("sessionId") as Scalars["ID"]["input"],
      );
    case Action.CreateNote:
      return createNote(
        context.graphql,
        JSON.parse(formData.get("input") as string),
      );
    case Action.UpdateNote:
    case Action.FavoriteNote:
    case Action.UnfavoriteNote:
      return updateNote(
        context.graphql,
        formData.get("noteId") as string,
        JSON.parse(formData.get("input") as string),
      );
    case Action.DeleteNote:
      return deleteNote(context.graphql, formData.get("noteId") as string);
    case Action.SaveReflectionResponse:
      return saveReflectionResponse(
        context.graphql,
        JSON.parse(formData.get("input") as string),
      );
    default:
      return {};
  }
}

export function meta({ data, matches }: MetaArgs<typeof loader>) {
  const rootData = getRootLoaderDataFromMatches(matches);
  const t = translate.bind(null, rootData?.labels ?? {});

  return [
    ...createSeoMetaData({
      title: data?.session.seo?.title,
      description: data?.session.seo?.description,
      image: getArtworkFromData(
        data?.session.module?.artwork as ClassroomImage[],
        "16:9",
      )?.url,
      labels: [
        {
          label: t("common.teacher"),
          data: data?.session.class?.teacher?.name!,
        },
        {
          label: t("common.duration"),
          data: formatDuration(data?.session.duration!, t),
        },
      ],
    }),
  ];
}

export { SessionDetail as default } from "./SessionDetail";
